import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next";
import Styles from "./SpecTable.module.css";

/**
 * SpecTable — a key/value specification block with optional product image and
 * certification badges. Nothing else in the library renders tabular key/value
 * data (Prismic rich text has no tables).
 *
 * Spec rows are the repeatable items (`spec_label` / `spec_value`). Badges ride
 * `primary` as a "|"-separated Text field, split here — a slice can hold only
 * one repeatable zone, and that is the spec rows. Each badge may itself contain
 * "·", so the delimiter is "|", not "·".
 *
 * @typedef {import("@prismicio/client").Content.SpecTableSlice} SpecTableSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<SpecTableSlice>} SpecTableProps
 * @param {SpecTableProps}
 */
const SpecTable = ({ slice: { primary, items } }) => {
	const hasImage = isFilled.image(primary?.image);
	const badges = (primary?.badges || "")
		.split("|")
		.map((b) => b.trim())
		.filter(Boolean);

	return (
		<section
			className={Styles.wrapper}
			data-theme={primary?.dark_theme ? "dark" : "light"}>
			<div className={Styles.container} data-contain="true">
				<header className={Styles.header}>
					{primary?.eyebrow && (
						<p className={Styles.eyebrow}>{primary?.eyebrow}</p>
					)}
					{isFilled.richText(primary?.heading) && (
						<div className={Styles.heading}>
							<PrismicRichText field={primary?.heading} />
						</div>
					)}
					{isFilled.richText(primary?.intro) && (
						<div className={Styles.intro}>
							<PrismicRichText field={primary?.intro} />
						</div>
					)}
				</header>

				<div className={Styles.layout} data-has-image={hasImage}>
					{hasImage && (
						<div className={Styles.media}>
							<PrismicNextImage
								field={primary?.image}
								sizes="(max-width: 1024px) 100vw, 40vw"
								style={{ width: "100%", height: "auto" }}
							/>
						</div>
					)}

					<div className={Styles.details}>
						<table className={Styles.table}>
							<tbody>
								{items?.map((item, index) => (
									<tr key={index}>
										<td>{item?.spec_label}</td>
										<td>{item?.spec_value}</td>
									</tr>
								))}
							</tbody>
						</table>

						{badges.length > 0 && (
							<ul className={Styles.certRow}>
								{badges.map((badge, index) => (
									<li className={Styles.badge} key={index}>
										{badge}
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};

export default SpecTable;
